if (!customElements.get("key-figures")) {
  customElements.define(
    "key-figures",
    class KeyFigures extends HTMLElement {
      constructor() {
        super();
        this.animated = false;
        this.prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
      }

      connectedCallback() {
        if (this.offsetParent === null) {
          return;
        }

        this.prepareFigures();

        if (this.prefersReducedMotion.matches) {
          this.finishAllFigures();
          return;
        }

        const animateOnScroll = this.dataset.animateOnScroll === "true";

        if (animateOnScroll) {
          this.setupIntersectionObserver();
        } else {
          this.animateAllFigures();
        }
      }

      setupIntersectionObserver() {
        const observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting && !this.animated) {
                this.animated = true;
                this.animateAllFigures();
                observer.disconnect();
              }
            });
          },
          {
            threshold: 0.2,
            rootMargin: "0px 0px -50px 0px",
          },
        );

        observer.observe(this);
      }

      animateAllFigures() {
        const figures = this.querySelectorAll(".key-figures-value");
        const duration = parseFloat(this.dataset.duration) || 2000;

        figures.forEach((figure) => {
          this.animateValue(figure, duration);
        });
      }

      animateValue(element, duration) {
        const reels = element.querySelectorAll(".key-figures-digit-reel");
        const digitCount = reels.length;

        if (!digitCount) {
          return;
        }

        const minimumDuration = Math.max(600, duration * 0.55);

        reels.forEach((reel, index) => {
          const progress = digitCount === 1 ? 1 : index / (digitCount - 1);
          const reelDuration = duration - (duration - minimumDuration) * progress;
          const totalSteps = Number.parseInt(reel.dataset.steps, 10) || 0;
          const finalTransform = `translateY(${-1 * totalSteps}em)`;

          if (typeof reel.animate !== "function") {
            reel.style.transform = finalTransform;
            return;
          }

          reel.animate(
            [
              { transform: "translateY(0)" },
              { transform: finalTransform },
            ],
            {
              duration: reelDuration,
              easing: "cubic-bezier(0.16, 1, 0.3, 1)",
              fill: "forwards",
            },
          ).onfinish = () => {
            reel.style.transform = finalTransform;
          };
        });
      }

      prepareFigures() {
        const figures = this.querySelectorAll(".key-figures-value");

        figures.forEach((figure) => {
          if (figure.dataset.prepared === "true") {
            return;
          }

          const rawValue = `${figure.dataset.value || figure.textContent || ""}`.trim();
          const characters = [...rawValue];
          const digits = characters.filter((character) => /\d/.test(character));

          if (!digits.length) {
            figure.textContent = rawValue;
            return;
          }

          figure.dataset.prepared = "true";
          figure.setAttribute("aria-label", rawValue);
          figure.textContent = "";

          let digitIndex = 0;

          characters.forEach((character) => {
            if (/\d/.test(character)) {
              figure.append(this.createDigitColumn(character, digitIndex, digits.length));
              digitIndex += 1;
            } else {
              figure.append(this.createStaticCharacter(character));
            }
          });
        });
      }

      createDigitColumn(character, digitIndex, digitCount) {
        const digit = document.createElement("span");
        const reel = document.createElement("span");
        const targetDigit = Number.parseInt(character, 10);
        const digitProgress = digitCount === 1 ? 1 : digitIndex / (digitCount - 1);
        const cycles = 3 + Math.round(digitProgress * 4);
        const totalSteps = cycles * 10 + targetDigit;

        digit.className = "key-figures-digit";
        reel.className = "key-figures-digit-reel";
        reel.dataset.steps = `${totalSteps}`;
        reel.setAttribute("aria-hidden", "true");

        for (let step = 0; step <= totalSteps; step += 1) {
          const cell = document.createElement("span");
          cell.className = "key-figures-digit-cell";
          cell.textContent = `${step % 10}`;
          reel.append(cell);
        }

        digit.append(reel);
        return digit;
      }

      createStaticCharacter(character) {
        const element = document.createElement("span");
        element.className = "key-figures-char";
        element.textContent = character;
        element.setAttribute("aria-hidden", "true");
        return element;
      }

      finishAllFigures() {
        this.querySelectorAll(".key-figures-value").forEach((figure) => {
          const rawValue = `${figure.dataset.value || figure.textContent || ""}`.trim();
          figure.textContent = rawValue;
        });
      }
    },
  );
}
